import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SAVING_OVERLAY_DEBOUNCE_TIME } from 'src/app/app.constants';
import { AppUser } from 'src/app/shared/models/app-user';
import * as AppSelectors from 'src/app/state/app/app.selectors';
import { AppSettingsDto, ListDashboardDto, ListItemCategoryDto, ListVisualDto, UserDashboardDto, UserVisualDto } from '../../../services/api.services';
import { combineLogicalOr } from '../../../shared/utils';
import { InsightsLibraryActions, InsightsLibraryDashboard, InsightsLibraryPage, InsightsLibrarySelectors, InsightsLibraryVisual } from '../../state/insights-library';

@Component({
  selector: 'app-insights-library-shell',
  template: `
    <app-insights-library
      [user]="user$ | async"

      [loadingApp]="loadingApp$ | async"
      [loadingItems]="loadingItems$ | async"
      [busy]="busy$ | async"

      [page]="page$ | async"
      [starredOnly]="starredOnly$ | async"
      [filter]="filter$ | async"

      [appSettings]="appSettings$ | async"

      [loadingDashboards]="loadingDashboards$ | async"
      [dashboards]="dashboards$ | async"

      [loadingVisuals]="loadingVisuals$ | async"
      [visuals]="visuals$ | async"

      [saving]="saving$ | async"

      [inViewMode]="inViewMode$ | async"
      [selectedVisual]="selectedVisual$ | async"
      [selectedDashboard]="selectedDashboard$ | async"

      (close)="close()"
      (setPage)="setPage($event)"
      (setStarredOnly)="setStarredOnly($event)"
      (setFilter)="setFilter($event)"
      (turnViewModeOff)="turnViewModeOff()"

      (favouriteDashboard)="favouriteDashboard($event)"
      (viewDashboard)="viewDashboard($event)"
      (copyDashboard)="copyDashboard($event)"
      (toggleDashboardHome)="toggleDashboardHome($event)"
      (exportToPDFDashboard)="exportToPDFDashboard($event)"

      (favouriteVisual)="favouriteVisual($event)"
      (addToHomeVisual)="addToHomeVisual($event)"
      (viewVisual)="viewVisual($event)"
      (copyVisual)="copyVisual($event)"
    ></app-insights-library>
  `,
  standalone: false
})
export class InsightsLibraryShellComponent implements OnInit {
  static readonly dialogConfig = <MatDialogConfig>{
    panelClass: 'insights-library-dialog',
    hasBackdrop: true,
    disableClose: true,

    width: '100%',
    maxWidth: '1670px',
    height: '850px',
    maxHeight: '95vh',
  };

  user$: Observable<AppUser>;

  loadingApp$: Observable<boolean>;
  loadingItems$: Observable<boolean>;
  busy$: Observable<boolean>;

  appSettings$: Observable<AppSettingsDto>;
  
  itemCategories$: Observable<ListItemCategoryDto[]>;

  loadingDashboards$: Observable<boolean>;
  dashboards$: Observable<ListDashboardDto[]>;

  loadingVisuals$: Observable<boolean>;
  visuals$: Observable<ListVisualDto[]>;

  saving$: Observable<boolean>;


  page$: Observable<InsightsLibraryPage>;
  starredOnly$: Observable<boolean>;
  filter$: Observable<string>;


  inViewMode$: Observable<boolean>;
  selectedVisual$: Observable<InsightsLibraryVisual>;
  selectedDashboard$: Observable<InsightsLibraryDashboard>;
  

  constructor(
    public store: Store,
    public router: Router,
    public route: ActivatedRoute) {
  }

  ngOnInit() {
    this.user$ =
      this.store.pipe(select(AppSelectors.getUser));


    this.itemCategories$ =
      this.store.pipe(select(AppSelectors.getItemCategories));

    this.appSettings$ =
      this.store.pipe(select(AppSelectors.getSettings));

    this.loadingDashboards$ =
      this.store.pipe(select(InsightsLibrarySelectors.loadingDashboards));

    this.dashboards$ =
      this.store.pipe(select(InsightsLibrarySelectors.dashboards));


    this.loadingVisuals$ =
      this.store.pipe(select(InsightsLibrarySelectors.loadingVisuals));

    this.visuals$ =
      this.store.pipe(select(InsightsLibrarySelectors.visuals));


    this.saving$ =
      combineLogicalOr(this.store.select(InsightsLibrarySelectors.updating)).pipe(
        debounceTime(SAVING_OVERLAY_DEBOUNCE_TIME),
        distinctUntilChanged()
      );


    this.page$ =
      this.store.pipe(select(InsightsLibrarySelectors.page));

    this.starredOnly$ =
      this.store.pipe(select(InsightsLibrarySelectors.starredOnly));

    this.filter$ =
      this.store.pipe(select(InsightsLibrarySelectors.filter));


    this.inViewMode$ =
      this.store.pipe(select(InsightsLibrarySelectors.inViewMode));

    this.selectedVisual$ =
      this.store.pipe(select(InsightsLibrarySelectors.visualItemSelector.selectedItem));

    this.selectedDashboard$ =
      this.store.pipe(select(InsightsLibrarySelectors.dashboardItemSelector.selectedItem));



    this.loadingApp$ = of(false);

    this.loadingItems$ =
      combineLogicalOr(
        this.loadingDashboards$,
        this.loadingVisuals$,
      );

    this.busy$ =
      combineLogicalOr(
        this.loadingApp$,
        this.loadingItems$,
      );

    this.store.dispatch(InsightsLibraryActions.loadDashboards());
    this.store.dispatch(InsightsLibraryActions.loadVisuals());
  }


  close() {
    this.store.dispatch(InsightsLibraryActions.setVisible({ visible: false }));
  }

  setPage(page: InsightsLibraryPage) {
    this.store.dispatch(InsightsLibraryActions.setPage({ page }));
  }

  setStarredOnly(starredOnly: boolean) {
    this.store.dispatch(InsightsLibraryActions.setStarredOnly({ starredOnly }));
  }

  setFilter(filter: string) {
    this.store.dispatch(InsightsLibraryActions.setFilter({ filter }));
  }

  turnViewModeOff() {
    this.store.dispatch(InsightsLibraryActions.toggleViewMode({ viewMode: false }));
  }


  favouriteDashboard(item: InsightsLibraryDashboard) {
    const userDashboard: UserDashboardDto = {
      dashboardId: item.dashboard.id,
      favourite: !item.favourite,
      home: item.dashboard.home
    };

    this.store.dispatch(InsightsLibraryActions.updateUserDashboard({ item, userDashboard }));
  }     
  

  viewDashboard(item: InsightsLibraryDashboard) {
    this.store.dispatch(InsightsLibraryActions.viewDashboard({ item }));
  }

  copyDashboard(item: InsightsLibraryDashboard) {
    this.store.dispatch(InsightsLibraryActions.copyDashboard({ item }));
  }
  
  toggleDashboardHome(item: InsightsLibraryDashboard) {
    const userDashboard: UserDashboardDto = {
      dashboardId: item.dashboard.id,
      favourite: item.favourite,
      home: !item.dashboard.home
    };

    this.store.dispatch(InsightsLibraryActions.updateUserDashboard({ item, userDashboard }));
  }


  exportToPDFDashboard(e: { item: InsightsLibraryDashboard }) {
    this.store.dispatch(InsightsLibraryActions.exportDashboardToPdf(e));
  }


  favouriteVisual(item: InsightsLibraryVisual) {
    const userVisual: UserVisualDto = {
      visualId: item.visual.id,
      favourite: !item.favourite,
      home: item.visual.home
    };

    this.store.dispatch(InsightsLibraryActions.updateUserVisual({ item, userVisual }));
  }

  addToHomeVisual(item: InsightsLibraryVisual) {
    const userVisual: UserVisualDto = {
      visualId: item.visual.id,
      favourite: item.favourite,
      home: !item.visual.home
    };

    this.store.dispatch(InsightsLibraryActions.updateUserVisual({ item, userVisual }));
  }

  viewVisual(item: InsightsLibraryVisual) {
    this.store.dispatch(InsightsLibraryActions.viewVisual({ item }));
  }

  copyVisual(item: InsightsLibraryVisual) {
    this.store.dispatch(InsightsLibraryActions.copyVisual({ item }));
  }
}

